import { AlertCircle, Loader2, ShieldOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAdminConfig, fetchAdminProfit } from '@/api/admin';
import AdminMobileTopBar from '@/components/admin/AdminMobileTopBar';
import AdminSidebar from '@/components/admin/AdminSidebar';
import AdminTicker from '@/components/admin/AdminTicker';
import ActiveSubpacksSubpage from '@/components/admin/subpages/ActiveSubpacksSubpage';
import AdManagerSubpage from '@/components/admin/subpages/AdManagerSubpage';
import AuditSubpage from '@/components/admin/subpages/AuditSubpage';
import CheatersSubpage from '@/components/admin/subpages/CheatersSubpage';
import DashboardSubpage from '@/components/admin/subpages/DashboardSubpage';
import DepositsSubpage from '@/components/admin/subpages/DepositsSubpage';
import LaunchControlSubpage from '@/components/admin/subpages/LaunchControlSubpage';
import PayoutsSubpage from '@/components/admin/subpages/PayoutsSubpage';
import ProfitSubpage from '@/components/admin/subpages/ProfitSubpage';
import ProofOfWorkSubpage from '@/components/admin/subpages/ProofOfWorkSubpage';
import SubpackSettingsSubpage from '@/components/admin/subpages/SubpackSettingsSubpage';
import SystemSubpage from '@/components/admin/subpages/SystemSubpage';
import ThreatTelemetrySubpage from '@/components/admin/subpages/ThreatTelemetrySubpage';
import TicketsSubpage from '@/components/admin/subpages/TicketsSubpage';
import UsersSubpage from '@/components/admin/subpages/UsersSubpage';
import ThreatBadge from '@/components/admin/ThreatBadge';
import EarningsDisclaimer from '@/components/EarningsDisclaimer';
import { useAdmin } from '@/contexts/AdminContext';
import useSubpageParam from '@/hooks/useSubpageParam';

export const ADMIN_SUBPAGES = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'users', label: 'User Accounts Directory' },
  { id: 'profit', label: 'Profit Ledger Matrix' },
  { id: 'deposits', label: 'Deposits Oversight' },
  { id: 'payouts', label: 'NOWPayments Mass Payouts' },
  { id: 'ad_manager', label: 'Ad Manager' },
  { id: 'audit', label: 'Global Financial Audit' },
  { id: 'system', label: 'System Variable Governor' },
  { id: 'subpack_settings', label: 'Subpack Settings' },
  { id: 'active_subpacks', label: 'Active Subpacks' },
  { id: 'proof_of_work', label: 'Proof of Work' },
  { id: 'launch_control', label: 'Launch Control' },
  { id: 'cheaters', label: 'Cheaters' },
  { id: 'threat', label: 'DDoS Threat Telemetry' },
];

// Out of the menu since support moved to email (Ron, 2026-10-24), but still
// opens at ?subpage=tickets so the old tickets can be answered.
const HIDDEN_ADMIN_SUBPAGES = [{ id: 'tickets', label: 'Support Control Station' }];

const ADMIN_SUBPAGE_IDS = [...ADMIN_SUBPAGES, ...HIDDEN_ADMIN_SUBPAGES].map((s) => s.id);

/**
 * Admin SPA shell. Mirrors the member workspace architecture: a single page
 * wrapper context, sidebar (desktop) + hamburger drawer (mobile), state-toggled
 * subpages, no router moves between them. The fixed header embeds the live
 * AdminTicker which polls /api/admin/ticker every 3 seconds.
 */
const AdminApp = () => {
  const { admin, loading, denied, refresh } = useAdmin();
  // `?subpage=<id>` opens that subpage and is kept in sync (god-only pages keep their placeholder guard).
  const [active, setActive] = useSubpageParam(ADMIN_SUBPAGE_IDS);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profitCeiling, setProfitCeiling] = useState(null);
  const [profitTotal, setProfitTotal] = useState(null);
  const [advisoryDismissed, setAdvisoryDismissed] = useState(false);
  const navigate = useNavigate();

  // Poll the off-chain admin profit + ceiling so the advisory banner stays live.
  // SUB-ADMINS DO NOT POLL — the profit endpoint is god-only (returns 403)
  // and the advisory banner is part of the operator-only profit oversight.
  useEffect(() => {
    if (!admin) return;
    if (!admin.is_god_account) return;
    let alive = true;
    const tick = async () => {
      try {
        const [cfg, profit] = await Promise.all([fetchAdminConfig(), fetchAdminProfit('all')]);
        if (!alive) return;
        const ceiling = parseFloat(cfg?.admin_alert_ceiling ?? '0');
        const total = parseFloat(profit?.summary?.total ?? '0');
        setProfitCeiling(Number.isNaN(Number(ceiling)) ? 0 : ceiling);
        setProfitTotal(Number.isNaN(Number(total)) ? 0 : total);
      } catch {
        // ignore
      }
    };
    tick();
    // Slow-poll (2025-06): the profit-ceiling advisory does not need to
    // react faster than the cycle engine's 60s heartbeat.
    const id = setInterval(tick, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [admin]);

  const advisoryActive = !advisoryDismissed && profitCeiling != null && profitTotal != null && profitCeiling > 0 && profitTotal >= profitCeiling;

  if (loading) {
    return (
      <div className="sc-canvas min-h-screen flex items-center justify-center px-6" data-testid="admin-loading">
        <div className="sc-glass rounded-2xl p-8 md:p-10 max-w-md w-full text-center">
          <div
            className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4"
            style={{
              background: 'radial-gradient(circle, rgba(255,194,14,0.25), rgba(255,194,14,0) 70%)',
            }}
          >
            <Loader2 className="w-9 h-9 animate-spin text-[#FFC20E]" />
          </div>
          <div className="font-display text-xl text-[#FFC20E] font-bold mb-1">Loading Admin Console</div>
          <div className="text-[12px] text-[var(--sc-text-dim)] tracking-[0.18em] uppercase">Verifying admin role &middot; 1SimpleCycler 3.0</div>
        </div>
      </div>
    );
  }

  if (denied || !admin) {
    return (
      <div className="sc-canvas min-h-screen flex items-center justify-center px-6" data-testid="admin-denied">
        <div className="sc-glass rounded-2xl p-8 max-w-md text-center">
          <ShieldOff className="w-8 h-8 mx-auto text-[#FFB612]" />
          <div className="text-[#FFC20E] font-display text-xl mt-3 mb-2">Admin role required</div>
          <p className="text-[var(--sc-text-dim)] text-sm mb-6">
            The admin probe failed. If you ARE the admin (signed in as Ronk2326), this is most often a transient network blip — tap{' '}
            <strong className="text-[#FFE08A]">Retry</strong> below. If the denial persists, sign in as an admin user.
          </p>
          <div className="flex gap-2 justify-center flex-wrap">
            <button
              type="button"
              className="sc-btn-ghost"
              onClick={() => {
                refresh?.();
              }}
              data-testid="admin-denied-retry"
              style={{
                background: 'linear-gradient(135deg, #FFC20E, #FFB612)',
                color: '#0a0a0b',
                border: '1px solid rgba(255,182,18,0.85)',
              }}
            >
              Retry
            </button>
            <button type="button" className="sc-btn-ghost" onClick={() => navigate('/login', { replace: true })} data-testid="admin-denied-switch">
              Switch user
            </button>
            <button type="button" className="sc-btn-ghost" onClick={() => navigate('/dashboard', { replace: true })} data-testid="admin-denied-member">
              Member dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const renderSubpage = () => {
    // Defense in depth — if a sub-admin somehow lands on a god-only
    // subpage (URL deep link, prior session state), render an access-
    // restricted placeholder instead of mounting the real component.
    // The backend would 403 every API call anyway; this ensures clean UX.
    const isGodOnly = active === 'profit' || active === 'payouts' || active === 'launch_control';
    if (isGodOnly && admin && !admin.is_god_account) {
      return (
        <div className="sc-glass rounded-2xl p-8 max-w-xl mx-auto text-center" data-testid="admin-access-restricted-placeholder">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
            style={{
              background: 'rgba(255,194,14,0.10)',
              border: '1px solid rgba(255,194,14,0.42)',
            }}
          >
            <ShieldOff className="w-6 h-6 text-[#FFC20E]" />
          </div>
          <h3 className="font-display text-xl font-bold">Access restricted</h3>
          <p className="text-[13px] text-[var(--sc-text-dim)] mt-2 leading-relaxed">
            This area is reserved for the primary platform operator. Your admin role does not include access to the {activeMeta?.label || 'selected page'}.
          </p>
          <button
            type="button"
            onClick={() => setActive('dashboard')}
            className="sc-btn-gold mt-5"
            style={{ padding: '10px 18px', fontSize: 12 }}
            data-testid="admin-restricted-go-dashboard-btn"
          >
            Back to Dashboard
          </button>
        </div>
      );
    }
    switch (active) {
      case 'dashboard':
        return <DashboardSubpage admin={admin} />;
      case 'users':
        return <UsersSubpage admin={admin} />;
      case 'profit':
        return <ProfitSubpage admin={admin} />;
      case 'deposits':
        return <DepositsSubpage admin={admin} />;
      case 'payouts':
        return <PayoutsSubpage admin={admin} />;
      case 'tickets':
        return <TicketsSubpage admin={admin} />;
      case 'ad_manager':
        return <AdManagerSubpage />;
      case 'audit':
        return <AuditSubpage admin={admin} />;
      case 'system':
        return <SystemSubpage admin={admin} onChanged={refresh} />;
      case 'subpack_settings':
        return <SubpackSettingsSubpage admin={admin} />;
      case 'active_subpacks':
        return <ActiveSubpacksSubpage admin={admin} />;
      case 'proof_of_work':
        return <ProofOfWorkSubpage admin={admin} />;
      case 'launch_control':
        return <LaunchControlSubpage admin={admin} />;
      case 'cheaters':
        return <CheatersSubpage admin={admin} />;
      case 'threat':
        return <ThreatTelemetrySubpage admin={admin} />;
      default:
        return null;
    }
  };

  const activeMeta = [...ADMIN_SUBPAGES, ...HIDDEN_ADMIN_SUBPAGES].find((s) => s.id === active);

  return (
    <div className="sc-canvas min-h-screen" data-testid="admin-app">
      <AdminSidebar
        active={active}
        onSelect={(id) => {
          setActive(id);
          setDrawerOpen(false);
        }}
        admin={admin}
      />

      <AdminMobileTopBar
        active={active}
        onSelect={(id) => {
          setActive(id);
          setDrawerOpen(false);
        }}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        admin={admin}
      />

      <div className="lg:pl-[280px] pt-16 lg:pt-0 min-h-screen relative z-10">
        <header className="hidden lg:flex sticky top-0 z-30 h-16 px-8 items-center justify-between border-b border-[rgba(255,182,18,0.10)] bg-[rgba(8,8,9,0.78)] backdrop-blur-xl gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <h2 className="font-display text-lg font-semibold whitespace-nowrap">{activeMeta?.label || ''}</h2>
            <span className="sc-pill" style={{ padding: '3px 10px', fontSize: 10 }}>
              <span className="sc-dot" />
              Live
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <AdminTicker />
          </div>
          <div className="shrink-0">
            <ThreatBadge onOpen={() => setActive('threat')} />
          </div>
        </header>

        <div className="lg:hidden px-4 py-3 border-b border-[rgba(255,182,18,0.10)] bg-[rgba(8,8,9,0.65)] flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <AdminTicker />
          </div>
          <ThreatBadge compact onOpen={() => setActive('threat')} />
        </div>

        {advisoryActive && (
          <div
            className="sc-glass-strong rounded-2xl mx-4 md:mx-8 mt-4 md:mt-6 p-4 md:p-5 flex items-start gap-3 border border-[rgba(255,194,14,0.55)]"
            style={{ background: 'linear-gradient(180deg, rgba(255,194,14,0.14), rgba(255,194,14,0.04))' }}
            data-testid="profit-ceiling-banner"
          >
            <AlertCircle className="w-5 h-5 text-[#FFC20E] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[#FFC20E] text-sm md:text-base">
                Platform Advisory: Accumulated admin off-chain revenue metrics have crossed your target limit.
              </div>
              <div className="text-[12px] text-[var(--sc-text-dim)] mt-1">
                Current pool ${profitTotal.toFixed(2)} · Ceiling ${profitCeiling.toFixed(2)}. Submit a unified system withdrawal request to extract available
                funds from NOWPayments.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setActive('payouts')}
                className="sc-btn-gold"
                style={{ padding: '8px 14px', fontSize: 11 }}
                data-testid="profit-ceiling-go-payouts"
              >
                Open Mass Payouts
              </button>
              <button
                type="button"
                onClick={() => setAdvisoryDismissed(true)}
                className="sc-btn-ghost"
                style={{ padding: '8px 10px', fontSize: 11 }}
                data-testid="profit-ceiling-dismiss"
                aria-label="Dismiss advisory"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        <div className="px-5 md:px-8 py-6 md:py-10 max-w-[1480px] mx-auto">{renderSubpage()}</div>

        <EarningsDisclaimer variant="compact" />
      </div>
    </div>
  );
};

export default AdminApp;
